import {
  useEffect,
  useEffectEvent,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { isSearchActivationKey } from "@softmax/web-features/site/records";

import { attachedPillElement } from "./pill-element";

type HeaderPanel = "coworld" | "nav" | "search" | "account" | `menu:${string}`;

export function useSiteHeader({
  pathname,
  hydrated,
  onOpenChange,
  onNavigationAccepted,
}: {
  pathname: string;
  hydrated: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigationAccepted: (href: string) => void;
}) {
  const ids = useId();
  const bar = useRef<HTMLElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [opened, setOpened] = useState<{
    panel: HeaderPanel;
    pathname: string;
  } | null>(null);
  const [observedPathname, setObservedPathname] = useState(pathname);

  if (observedPathname !== pathname) {
    setObservedPathname(pathname);

    // Native history can update before React observes the address where this panel opened.
    if (opened?.pathname !== pathname) setOpened(null);
  }

  const panel = opened?.panel ?? null;
  useLayoutEffect(() => onOpenChange(panel !== null), [panel, onOpenChange]);
  useEffect(() => {
    if (panel === "search")
      attachedPillElement(input).focus({ preventScroll: true });
  }, [panel]);

  function setPanel(next: HeaderPanel | null) {
    setOpened(
      next === null
        ? null
        : { panel: next, pathname: window.location.pathname },
    );
  }

  const onHeaderKey = useEffectEvent((event: globalThis.KeyboardEvent) => {
    if (event.defaultPrevented || document.querySelector("dialog:modal"))
      return;

    if (isSearchActivationKey(event)) {
      event.preventDefault();
      setPanel("search");
      input.current?.focus();
    } else if (
      panel !== null &&
      event.key === "Escape" &&
      event.target instanceof Node &&
      attachedPillElement(bar).contains(event.target)
    ) {
      event.preventDefault();
      const opener = attachedPillElement(bar).querySelector<HTMLElement>(
        `[aria-controls="${ids}-${panel}"]`,
      );
      setOpened(null);
      opener?.focus({ preventScroll: true });
    }
  });
  const onOutsidePress = useEffectEvent((event: PointerEvent) => {
    if (
      event.target instanceof Node &&
      !attachedPillElement(bar).contains(event.target)
    )
      setOpened(null);
  });
  const onFocusChange = useEffectEvent((event: FocusEvent) => {
    if (
      event.target instanceof Node &&
      !attachedPillElement(bar).contains(event.target)
    )
      setOpened(null);
  });
  useEffect(() => {
    window.addEventListener("keydown", onHeaderKey);
    return () => window.removeEventListener("keydown", onHeaderKey);
  }, []);
  useEffect(() => {
    if (panel === null) return;

    document.addEventListener("pointerdown", onOutsidePress, true);
    document.addEventListener("focusin", onFocusChange);
    return () => {
      document.removeEventListener("pointerdown", onOutsidePress, true);
      document.removeEventListener("focusin", onFocusChange);
    };
  }, [panel]);

  function onNavigate(href: string) {
    onNavigationAccepted(href);
    setOpened(null);
  }

  function disclosure(next: HeaderPanel) {
    return {
      type: "button" as const,
      "aria-expanded": panel === next,
      "aria-controls": `${ids}-${next}`,
      disabled: !hydrated,
      onClick: () => setPanel(panel === next ? null : next),
    };
  }

  return { ids, bar, input, panel, setPanel, disclosure, onNavigate };
}

export type SiteHeaderControls = ReturnType<typeof useSiteHeader>;
